import styled from '@emotion/styled';
import type { MessageGenPhase } from '@trinity/chat';
import { PhaseBadge } from './badges/PhaseBadge';
import { SessionHistoryDropdown } from './SessionHistoryDropdown';

export interface MessageGenerationSessionHeaderProps {
  sessionId?: number;
  sessionName: string;
  createdOn: Date | string;
  phase: MessageGenPhase;
  type: 'run' | 'segment';
  brandName?: string;
  objective?: string | null;
  showPageTitle?: boolean;
  showSessionHistory?: boolean;
  sessionHistoryIconOnly?: boolean;
  onSelectSession?: (sessionId: number) => void;
}

export function MessageGenerationSessionHeader({
  sessionId,
  sessionName,
  createdOn,
  phase,
  type,
  brandName,
  objective,
  showPageTitle = true,
  showSessionHistory = true,
  sessionHistoryIconOnly = false,
  onSelectSession,
}: MessageGenerationSessionHeaderProps): React.JSX.Element {
  const hasSessionName = sessionName && sessionName.trim().length > 0;
  const showDivider = showPageTitle && hasSessionName;

  return (
    <HeaderRoot>
      <HeaderLeft>
        {showPageTitle && (
          <>
            <PageTitle>Message Generation</PageTitle>
            {showDivider && <Divider>|</Divider>}
          </>
        )}
        {hasSessionName && <SessionName>{sessionName}</SessionName>}
      </HeaderLeft>

      <HeaderRight>
        <PhaseBadge phase={phase} />
        {brandName && <BrandItem>{brandName}</BrandItem>}
        {objective && <ObjectiveItem>{objective}</ObjectiveItem>}
        {showSessionHistory && onSelectSession && (
          <SessionHistoryDropdown
            currentSessionId={sessionId}
            onSelectSession={onSelectSession}
            iconOnly={sessionHistoryIconOnly}
          />
        )}
      </HeaderRight>
    </HeaderRoot>
  );
}

const HeaderRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #626262;
  white-space: nowrap;
`;

const Divider = styled.div`
  font-size: 16px;
  color: #e5e5e5;
  font-weight: 300;
`;

const SessionName = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #323232;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderRight = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
`;

const BrandItem = styled(MetadataItem)`
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
`;

const ObjectiveItem = styled(MetadataItem)`
  background: #e9d5ff;
  border: 1px solid #d8b4fe;
  color: #6b21a8;
`;

const DateTimeBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  background: #e0e7ff;
  color: #4338ca;
`;
